import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import { referenceService } from '@/services/referenceService';
import { GroupedIcd10Code } from '@/types/reference.types';
import { groupByCategory } from '@/utils/groupIcd10Codes';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/use-toast';

interface GeneticConditionAutocompleteProps {
  value?: string | null; // ICD-10 code (e.g., "Q90.0")
  onChange: (code: string | null, description?: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
}

export function GeneticConditionAutocomplete({
  value,
  onChange,
  placeholder = 'Caută afecțiune genetică (cod ICD-10 sau descriere)...',
  isDisabled = false,
  className
}: GeneticConditionAutocompleteProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState<GroupedIcd10Code[]>([]);
  const [selectedCode, setSelectedCode] = useState<GroupedIcd10Code | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadCodes();
  }, []);

  useEffect(() => {
    if (value && codes.length > 0) {
      const code = codes.find(c => c.icd10_code === value);
      if (code) {
        setSelectedCode(code);
        setSearchTerm('');
      }
    } else if (!value) {
      setSelectedCode(null);
      setSearchTerm('');
    }
  }, [value, codes]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCodes = async () => {
    setIsLoading(true);
    try {
      const data = await referenceService.getGeneticConditions();
      setCodes(data);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca afecțiunile genetice. Vă rugăm încercați din nou.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCodes = useMemo(() => {
    if (!searchTerm.trim()) {
      return codes;
    }

    const term = searchTerm.toLowerCase().trim();
    return codes.filter(code =>
      code.icd10_code.toLowerCase().includes(term) ||
      code.description_ro.toLowerCase().includes(term) ||
      code.category_group.toLowerCase().includes(term)
    );
  }, [codes, searchTerm]);

  const groupedCodes = useMemo(() => groupByCategory(filteredCodes), [filteredCodes]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (selectedCode) {
      setSelectedCode(null);
      onChange(null);
    }
  };

  const handleSelectCode = (code: GroupedIcd10Code) => {
    setSelectedCode(code);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(code.icd10_code, code.description_ro);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSelectedCode(null);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCodes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCodes.length) {
          handleSelectCode(filteredCodes[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-flat-index="${highlightedIndex}"]`
      ) as HTMLElement | null;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [highlightedIndex]);

  const displayValue = selectedCode
    ? `${selectedCode.icd10_code} - ${selectedCode.description_ro}`
    : searchTerm;

  let flatIndex = -1;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled || isLoading}
          className={cn('pl-10 pr-10', selectedCode && 'font-medium')}
        />
        {selectedCode && !isDisabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isOpen && !isDisabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-[320px] overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Se încarcă...
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="p-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit rezultate' : 'Nu sunt afecțiuni disponibile'}
              </p>
            </div>
          ) : (
            groupedCodes.map((group) => (
              <div key={group.category}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                  {group.category}
                </div>
                {group.items.map((code) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  return (
                    <div
                      key={code.icd10_code}
                      data-flat-index={index}
                      className={cn(
                        'px-3 py-2 cursor-pointer text-sm transition-colors text-foreground',
                        'hover:bg-accent hover:text-accent-foreground',
                        highlightedIndex === index && 'bg-accent text-accent-foreground',
                        selectedCode?.icd10_code === code.icd10_code && 'bg-primary/10 font-medium'
                      )}
                      onClick={() => handleSelectCode(code)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <div className="font-medium text-foreground">
                        {code.icd10_code}
                        {code.code_level === 'categorie' && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            (categorie generală)
                          </span>
                        )}
                      </div>
                      <div className="text-foreground/70 truncate">
                        {code.description_ro}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
